import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Snackbar,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded';
import UndoRoundedIcon from '@mui/icons-material/UndoRounded';
import FactCheckRoundedIcon from '@mui/icons-material/FactCheckRounded';

import { applicationsApi, type ApplicationEditableFieldDto } from '../../api/admin';
import {
  editErrorMessage,
  editedByLine,
  formatFieldValue,
  groupEditableFields,
  isOverridden,
  lockExplanation,
  renderValue,
  requiresReload,
} from '../../lib/applicationFieldEditing';
import { ApplicationSectionCard } from './applicationSectionChrome';
import { ApplicationFieldEditDialog } from './ApplicationFieldEditDialog';

/**
 * One editable value, with its provenance.
 *
 * The layout keeps three things visible at once where they apply: the effective
 * value, that a human changed it, and what the owning service still holds. Showing
 * only the correction would hide the evidence, which is the same mistake as
 * overwriting it.
 */
function EditableFieldRow({
  field,
  onEdit,
  onWithdraw,
  onHistory,
}: {
  field: ApplicationEditableFieldDto;
  onEdit: () => void;
  onWithdraw: () => void;
  onHistory: () => void;
}) {
  const overridden = isOverridden(field);
  const provenance = editedByLine(field);

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: 'minmax(150px, 190px) minmax(0, 1fr) auto' },
        columnGap: 1.5,
        rowGap: 0.35,
        alignItems: 'start',
        py: 0.25,
      }}
    >
      <Typography
        component="div"
        sx={{ fontSize: '0.8125rem', fontWeight: 600, color: 'text.secondary', lineHeight: 1.5 }}
      >
        {field.label}
      </Typography>

      <Box sx={{ minWidth: 0 }}>
        <Stack direction="row" spacing={0.75} alignItems="baseline" flexWrap="wrap" useFlexGap>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 500,
              lineHeight: 1.5,
              wordBreak: 'break-word',
              ...(field.sourceResolved === false ? { color: 'warning.main' } : null),
            }}
          >
            {formatFieldValue(field)}
          </Typography>
          {overridden ? (
            <Tooltip title={provenance ?? 'Corrected by a reviewer'} arrow>
              <Chip
                size="small"
                label="edited"
                sx={{
                  height: 18,
                  fontSize: '0.65rem',
                  fontWeight: 700,
                  letterSpacing: '0.04em',
                  textTransform: 'uppercase',
                }}
                color="warning"
                variant="outlined"
              />
            </Tooltip>
          ) : null}
        </Stack>

        {/* The evidence the correction shadows, kept in view. */}
        {overridden && field.sourceResolved !== false ? (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            {field.targetService} holds {renderValue(field.sourceValue, field.valueType)}
          </Typography>
        ) : null}

        {!overridden && provenance ? (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            {provenance}
          </Typography>
        ) : null}

        {field.sourceResolved === false ? (
          <Typography variant="caption" color="warning.main" sx={{ display: 'block' }}>
            {field.sourceUnresolvedReason ?? 'The owning service could not be reached.'}
          </Typography>
        ) : null}

      </Box>

      <Stack direction="row" spacing={0.25} sx={{ justifySelf: 'end' }}>
        {field.lastChangeId ? (
          <Tooltip title="Change history for this field" arrow>
            <IconButton size="small" onClick={onHistory} aria-label={`History for ${field.label}`}>
              <HistoryRoundedIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        ) : null}
        {overridden && field.editable ? (
          <Tooltip title="Withdraw this correction" arrow>
            <IconButton
              size="small"
              onClick={onWithdraw}
              aria-label={`Withdraw correction to ${field.label}`}
            >
              <UndoRoundedIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        ) : null}
        {field.editable ? (
          <Tooltip title={`Edit ${field.label.toLowerCase()}`} arrow>
            <IconButton size="small" onClick={onEdit} aria-label={`Edit ${field.label}`}>
              <EditRoundedIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        ) : null}
      </Stack>
    </Box>
  );
}

export interface ApplicationEditableFieldsPanelProps {
  applicationId: string;
  /** The status the reviewer is looking at. Echoed on save so a transition is caught. */
  applicationStatus: string;
  onShowFieldHistory?: (fieldKey: string) => void;
}

/**
 * The editing surface: every field the caller may correct, grouped as a reviewer
 * reads them.
 *
 * Renders locked fields rather than hiding them, with the reason. A field that
 * silently disappears reads as a missing feature; one that says
 * "not editable while the application is funded" reads as a rule.
 */
export function ApplicationEditableFieldsPanel({
  applicationId,
  applicationStatus,
  onShowFieldHistory,
}: ApplicationEditableFieldsPanelProps) {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<ApplicationEditableFieldDto | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [mustReload, setMustReload] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  const fieldsQ = useQuery({
    queryKey: ['applicationEditableFields', applicationId],
    queryFn: () => applicationsApi.editableFields(applicationId),
    enabled: !!applicationId,
    retry: false,
  });

  /** Everything the panel and its neighbours read is now stale. */
  const invalidateAll = () => {
    void queryClient.invalidateQueries({ queryKey: ['applicationEditableFields', applicationId] });
    void queryClient.invalidateQueries({ queryKey: ['applicationChanges', applicationId] });
    void queryClient.invalidateQueries({ queryKey: ['applicationJourney', applicationId] });
  };

  const editMutation = useMutation({
    mutationFn: (input: { field: ApplicationEditableFieldDto; value: string; reasonCode: string; reasonDetail: string }) =>
      applicationsApi.editFields(applicationId, {
        expectedStatus: applicationStatus,
        reasonCode: input.reasonCode,
        reasonDetail: input.reasonDetail,
        changes: [
          {
            fieldKey: input.field.key,
            value: input.value,
            // Echoed so a concurrent edit by someone else is caught rather than
            // silently overwritten.
            lastChangeId: input.field.lastChangeId ?? null,
          },
        ],
      }),
    onSuccess: (result) => {
      setEditing(null);
      setSaveError(null);
      setMustReload(false);
      if (result.changed.length === 0 && result.unchanged.length > 0) {
        setToast('That value already matched — nothing was changed.');
      } else {
        const extra = result.sideEffects.length
          ? ` Also cleared: ${result.sideEffects.join(', ')}.`
          : '';
        setToast(`Saved.${extra}`);
      }
      invalidateAll();
    },
    onError: (error) => {
      setSaveError(editErrorMessage(error));
      setMustReload(requiresReload(error));
    },
  });

  const withdrawMutation = useMutation({
    mutationFn: (field: ApplicationEditableFieldDto) =>
      applicationsApi.clearFieldOverride(applicationId, field.key, {
        expectedStatus: applicationStatus,
        reasonCode: 'OTHER',
        reasonDetail: `Correction withdrawn from the ${field.label} field by a reviewer.`,
      }),
    onSuccess: (result) => {
      setToast(
        result.cleared
          ? 'Correction withdrawn. The value from the owning service is showing again.'
          : 'There was no correction to withdraw.',
      );
      invalidateAll();
    },
    onError: (error) => setToast(editErrorMessage(error)),
  });

  const groups = useMemo(
    () => groupEditableFields(fieldsQ.data?.fields ?? []),
    [fieldsQ.data?.fields],
  );

  if (fieldsQ.isPending) {
    return (
      <ApplicationSectionCard
        title="Application data"
        subtitle="Loading editable fields…"
        icon={<FactCheckRoundedIcon fontSize="small" />}
      >
        <Box sx={{ py: 3, textAlign: 'center' }}>
          <CircularProgress size={22} />
        </Box>
      </ApplicationSectionCard>
    );
  }

  if (fieldsQ.isError || !fieldsQ.data) {
    return (
      <ApplicationSectionCard title="Application data" icon={<FactCheckRoundedIcon fontSize="small" />}>
        <Alert severity="error" variant="outlined">
          Could not load the editable fields for this application.
        </Alert>
      </ApplicationSectionCard>
    );
  }

  const editableCount = fieldsQ.data.fields.filter((f) => f.editable).length;

  /**
   * One explanation for the whole panel instead of one per row.
   *
   * When nothing is editable the reason is almost always the same for every field
   * — the application's stage — so repeating it eighteen times was noise that
   * buried the values themselves. Shown once here; the per-field detail is still
   * available on hover of a locked row.
   */
  const lockSummary = (() => {
    if (editableCount > 0) return null;
    const ctx = {
      status: fieldsQ.data!.status,
      assignedToEmail: fieldsQ.data!.assignedToEmail,
      assignedToUserId: fieldsQ.data!.assignedToUserId,
    };
    const reasons = new Set(
      fieldsQ
        .data!.fields.map((f) => lockExplanation(f, ctx))
        .filter((r): r is string => !!r),
    );
    if (reasons.size === 1) {
      return `Read-only — ${[...reasons][0]!.replace(/^Not editable/, 'not editable')}`;
    }
    return 'Read-only at this stage';
  })();

  return (
    <>
      <ApplicationSectionCard
        title="Application data"
        icon={<FactCheckRoundedIcon fontSize="small" />}
        subtitle={lockSummary ?? `${editableCount} field${editableCount === 1 ? '' : 's'} you can correct`}
      >
        <Stack spacing={2}>
          {/* Assignment is a property of the application, not of any one field, so
              it is explained once here rather than repeated against every row. */}
          {fieldsQ.data.assignedToUserId ? (
            <Alert severity="info" variant="outlined" sx={{ py: 0.5 }}>
              Assigned to{' '}
              <strong>{fieldsQ.data.assignedToEmail ?? fieldsQ.data.assignedToUserId}</strong> —
              only they can edit this application.
            </Alert>
          ) : null}

          {fieldsQ.data.manualEditCount > 0 ? (
            <Alert severity="info" variant="outlined" sx={{ py: 0.5 }}>
              This application has {fieldsQ.data.manualEditCount} recorded manual change
              {fieldsQ.data.manualEditCount === 1 ? '' : 's'}.
            </Alert>
          ) : null}

          {groups.map((group) => (
            <Box key={group.group}>
              <Stack direction="row" spacing={1} alignItems="baseline" sx={{ mb: 0.75 }}>
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 700,
                    letterSpacing: '0.06em',
                    textTransform: 'uppercase',
                    color: 'text.secondary',
                  }}
                >
                  {group.label}
                </Typography>
                {group.hint ? (
                  <Typography variant="caption" color="text.disabled">
                    {group.hint}
                  </Typography>
                ) : null}
              </Stack>
              <Stack spacing={1} divider={<Divider flexItem />}>
                {group.fields.map((field) => (
                  <EditableFieldRow
                    key={field.key}
                    field={field}
                    onEdit={() => {
                      setSaveError(null);
                      setMustReload(false);
                      setEditing(field);
                    }}
                    onWithdraw={() => withdrawMutation.mutate(field)}
                    onHistory={() => onShowFieldHistory?.(field.key)}
                  />
                ))}
              </Stack>
            </Box>
          ))}
        </Stack>
      </ApplicationSectionCard>

      <ApplicationFieldEditDialog
        open={!!editing}
        field={editing}
        reasonCodes={fieldsQ.data.reasonCodes}
        saving={editMutation.isPending}
        errorMessage={saveError}
        mustReload={mustReload}
        onClose={() => {
          setEditing(null);
          setSaveError(null);
          setMustReload(false);
        }}
        onSubmit={(input) => {
          if (!editing) return;
          editMutation.mutate({ field: editing, ...input });
        }}
      />

      <Snackbar
        open={!!toast}
        autoHideDuration={6000}
        onClose={() => setToast(null)}
        message={toast ?? ''}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </>
  );
}
